import { APP_ROUTES, useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import { Flex, Spin, Typography, useMessagedAsyncFn, useMessagedAsyncRetry } from "@payping/web-components";
import { Navigate, useSearchParams } from "react-router";
import { bnplConsumerGetOrder } from "@payping/redux/actions";
import Loading from "@/pages/loading";
import { RetryGetOrderSteps } from "@/redux/reducers/dashboard";
import { dashboardReduxReducerActions } from "@/redux/actions/dashboard";
import EpaPaymentCallbackPageSuccess from "./Success";
import EpaPaymentCallbackPageFailed from "./Failed";
import PayGetOrderLayout from "../GetOrder/Layout";
import PayExpiredOrder from "../ExpiredOrder";
import PayGetOrderUnAvailable from "../GetOrder/UnAvailable";
import PayUnAvailable from "../Unavailable";
import PayGetOrderIssuePromissoryFailed from "../GetOrder/IssuePromissory/Failed";

const EpaPaymentCallbackPage = () => {
  const { t } = useAppTranslation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id") ?? "";
  const dispatch = useAppDispatch();
  const trackingCode = useAppSelector((state) => state.dashboardPersist.trackingCode);

  const [verifyState, promissoryPaymentVerify] = useMessagedAsyncFn(
    async () => {
      try {
        const res = await bnplConsumerApis.promissoryPaymentVerify({ id, status: "" }, {
          isRow: true,
        } as SDKRequestInit);
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        throw e;
      }
    },
    [id],
    { loading: false },
    { error: false },
  );

  const getOrderState = useMessagedAsyncRetry(
    async () => {
      try {
        const res = await dispatch(bnplConsumerGetOrder(trackingCode, { isRaw: true }));
        promissoryPaymentVerify();
        return res;
      } catch (e: any) {
        // Check if e is an instance of Response and if the status is 400
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [trackingCode, dispatch, promissoryPaymentVerify],
    { success: false, error: true },
  );

  if ((getOrderState?.error as any)?.key === 109) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayExpiredOrder />
      </PayGetOrderLayout>
    );
  }

  // bad request
  if ((getOrderState?.error as any)?.status === 400) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayGetOrderUnAvailable
          name={getOrderState?.value?.merchantName}
          url={getOrderState?.value?.merchantRedirectUrl}
          errorMessages={(getOrderState?.error as any)?.errors ?? []}
        />
      </PayGetOrderLayout>
    );
  }

  if (getOrderState.loading || getOrderState.error?.name === "AuthError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  if (getOrderState?.error?.name === "ServerError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            getOrderState.retry();
          }}
          onRetryLoading={getOrderState.loading}
        />
      </PayGetOrderLayout>
    );
  }

  if (verifyState.loading) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Flex vertical className="tw:h-full tw:w-full tw:p-4!" justify="center" align="center" gap={"32px"}>
          <Spin size="large" />
          <Flex vertical gap={"small"} justify="center" align="center">
            <Typography.Title level={5} className="tw:m-0! tw:font-semibold!">
              {t("pay.getOrder.steps.payPromissory.checkingLoadingTitle")}
            </Typography.Title>
            <Typography.Paragraph className="tw:m-0!" type="secondary">
              {t("common.waitFewMinutes")}
            </Typography.Paragraph>
          </Flex>
        </Flex>
      </PayGetOrderLayout>
    );
  }

  if (verifyState.value?.success) {
    return (
      <PayGetOrderLayout showHeaders>
        <EpaPaymentCallbackPageSuccess data={verifyState?.value?.data ?? ""} />
      </PayGetOrderLayout>
    );
  }

  // error in generate issue
  if ((verifyState.error as any)?.key === 106 && (verifyState.error as any)?.detail) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <PayGetOrderIssuePromissoryFailed
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
          message={(verifyState.error as any)?.detail}
        />
      </PayGetOrderLayout>
    );
  }

  if (verifyState.error) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <EpaPaymentCallbackPageFailed />
      </PayGetOrderLayout>
    );
  }
  return <Navigate to={`${APP_ROUTES.pay.getOrder.root.url}?trackingCode=${trackingCode}`} />;
};

export default EpaPaymentCallbackPage;
